/*
 * Count-up numbers.
 *
 * Wrap the number — only the number — in .count-up. Its own text is the
 * target, so the markup still reads correctly with no JS:
 *
 *   $h1Text = '<span class="count-up">35</span>+';
 *   $h1Text = '<span class="count-up">2,500</span>+';
 *
 * When the element is half on screen it counts from 0 up to that number,
 * once, easing out so it settles gently on the final figure. Anything
 * outside the span (the "+", a unit) stays put.
 *
 * Commas are kept: "2,500" counts 0 … 1,250 … 2,500. Only whole numbers are
 * supported; a value it cannot read is left untouched.
 *
 * Optional: data-count-duration="3000" on the span (ms, default 2000).
 *
 * Reduced motion: the number is shown as written, no counting.
 * The width steadying (tabular figures) is .count-up in src/css/app.css.
 *
 * No-ops unless a .count-up is on the page.
 */

const DEFAULT_DURATION = 2000;

export default function initCountUp() {
  const elements = Array.from(document.querySelectorAll(".count-up"));

  if (!elements.length || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          countUp(entry.target);
        }
      });
    },
    { threshold: 0.5 },
  );

  elements.forEach((element) => {
    const target = parse(element.textContent);

    if (target === null) {
      return;
    }

    // Start from zero, formatted the same way the target is.
    element.dataset.countTarget = String(target);
    element.dataset.countCommas = element.textContent.includes(",") ? "1" : "";
    element.textContent = format(0, element);

    observer.observe(element);
  });
}

/* ------------------------------------------------------------------------
 * Animation
 * --------------------------------------------------------------------- */

/** Run one element from 0 to its target, one frame at a time. */
function countUp(element) {
  const target = Number(element.dataset.countTarget);
  const duration = Number(element.dataset.countDuration) || DEFAULT_DURATION;
  let start = null;

  const step = (now) => {
    start ??= now;

    const progress = Math.min((now - start) / duration, 1);
    element.textContent = format(Math.round(target * easeOut(progress)), element);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };

  requestAnimationFrame(step);
}

/** Fast at first, slowing into the final number (cubic ease-out). */
function easeOut(t) {
  return 1 - (1 - t) ** 3;
}

/* ------------------------------------------------------------------------
 * Reading and writing the number
 * --------------------------------------------------------------------- */

/** "2,500" → 2500. null if the text is not a whole number. */
function parse(text) {
  const digits = text.trim().replace(/,/g, "");
  return /^\d+$/.test(digits) ? Number(digits) : null;
}

/** 2500 → "2,500" when the authored number used commas, else "2500". */
function format(value, element) {
  return element.dataset.countCommas ? value.toLocaleString("en-AU") : String(value);
}
